"use client";

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { usePathname } from 'next/navigation';

declare global {
    interface Window {
        adsbygoogle: any[];
    }
}

export interface AdDisplayProps {
    slot: string;
    adStyle?: 'display' | 'multiplex' | 'in-feed' | 'in-article';
    adLayout?: 'horizontal' | 'vertical' | 'square';
    layoutKey?: string;
    className?: string;
}

/**
 * AdDisplay — SPA-safe Google AdSense component for Next.js.
 *
 * Key design decisions:
 * 1. The `key` on the wrapper div includes `pathname` + `navCount`, forcing React
 *    to completely destroy and recreate the <ins> element on every route change.
 *    This is critical because Google's adsbygoogle SDK marks processed <ins> tags
 *    with `data-adsbygoogle-status` and will NOT re-fill a previously processed tag.
 *
 * 2. IntersectionObserver gates the `push({})` call: ads only request when they
 *    scroll into view. This improves Google's Active View / viewability metric,
 *    which directly increases CPM (ads that are seen = higher bids).
 *
 * 3. On unmount (route away), we clean up any Google-injected iframes to prevent
 *    stale ad DOM from lingering in memory.
 */
export default function AdDisplay({
                                      slot,
                                      adStyle = 'display',
                                      adLayout = 'horizontal',
                                      layoutKey,
                                      className = ""
                                  }: AdDisplayProps) {
    const pathname = usePathname();
    const containerRef = useRef<HTMLDivElement>(null);
    const insRef = useRef<HTMLModElement>(null);
    const pushed = useRef(false);
    const [adFailed, setAdFailed] = useState(false);
    // Incremented on every pathname change to guarantee a fresh React key
    const [navCount, setNavCount] = useState(0);

    // Bump navCount whenever pathname changes — this forces a full remount
    useEffect(() => {
        setNavCount(c => c + 1);
        setAdFailed(false);
        pushed.current = false;
    }, [pathname]);

    // Core ad-loading logic: waits for the <ins> to be visible, then pushes
    const pushAd = useCallback(() => {
        if (pushed.current) return;
        try {
            if (typeof window !== 'undefined' && insRef.current) {
                const status = insRef.current.getAttribute('data-adsbygoogle-status');
                if (!status) {
                    (window.adsbygoogle = window.adsbygoogle || []).push({});
                    pushed.current = true;
                }
            }
        } catch (err) {
            console.debug(`[AdSense ${slot}]`, err);
        }
    }, [slot]);

    useEffect(() => {
        const insEl = insRef.current;
        if (!insEl) return;

        // Use IntersectionObserver to lazy-load ads when they enter viewport.
        // This improves viewability (= higher CPM) and avoids wasting impressions
        // on ads the user never scrolls to.
        let observer: IntersectionObserver | null = null;

        if ('IntersectionObserver' in window) {
            observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            // Small delay to let the layout settle after Next.js navigation
                            setTimeout(pushAd, 150);
                            observer?.unobserve(entry.target);
                        }
                    });
                },
                { rootMargin: '200px' } // Start loading 200px before visible
            );
            observer.observe(insEl);
        } else {
            // Fallback for browsers without IntersectionObserver
            setTimeout(pushAd, 300);
        }

        // Check for unfilled ads and collapse the slot gracefully
        const fallbackTimer = setTimeout(() => {
            if (insEl) {
                const adStatus = insEl.getAttribute('data-ad-status');
                if (adStatus === 'unfilled') {
                    setAdFailed(true);
                }
            }
        }, 6000);

        return () => {
            clearTimeout(fallbackTimer);
            if (observer) observer.disconnect();
        };
    }, [navCount, pushAd]);

    // Map adStyle/adLayout to the correct data-ad-format value
    const getAdFormat = () => {
        if (adStyle === 'multiplex') return 'autorelaxed';
        if (adStyle === 'in-feed' || adStyle === 'in-article') return 'fluid';
        if (adLayout === 'square') return 'rectangle';
        return adLayout;
    };

    return (
        <div
            // This key forces React to fully destroy the previous <ins> DOM and create a fresh one on navigation.
            key={`ad-${slot}-${navCount}`}
            ref={containerRef}
            className={`w-full overflow-hidden my-6 border border-slate-200/80 bg-white/80 rounded-xl p-2.5 text-center transition-all ${
                adFailed ? 'hidden' : ''
            } ${className}`}
        >
            <div className="flex items-center justify-between px-2 pb-1.5 border-b border-slate-100 mb-2">
                <span className="text-[9px] font-mono font-semibold tracking-widest text-slate-400 uppercase">
                    Google Advertisement
                </span>
                <span className="text-[9px] font-mono text-slate-400">
                    Slot #{slot.slice(-4)}
                </span>
            </div>

            <ins
                ref={insRef}
                className="adsbygoogle"
                data-ad-client="ca-pub-7566961788653827"
                data-ad-slot={slot}
                data-ad-format={getAdFormat()}
                data-ad-layout-key={adStyle === 'in-feed' ? layoutKey : undefined}
                data-ad-layout={adStyle === 'in-article' ? 'in-article' : undefined}
                data-full-width-responsive="true"
                style={{
                    display: 'block',
                    width: '100%',
                    minHeight: adLayout === 'horizontal' ? '90px' : adLayout === 'square' ? '250px' : 'auto'
                }}
            />
        </div>
    );
}
